import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import apiClient from '../../utils/api';

// The two dials: how much the app asks of you each day, and how much it offers
// back (N8).
//
// Each option shows what it costs, not what it sells. Turning the interview down
// is a real loss of data and says so — the no-silent-demotion rule works both
// ways: nothing shrinks without telling you, and nothing grows without you
// asking. The undo is the same control you used to get here.

const THOROUGHNESS_LABEL = {
  light: 'Light',
  standard: 'Standard',
  deep: 'Thorough',
};

const APPETITE_LABEL = {
  off: 'Never',
  ask: 'When it matters',
  keen: 'Whenever there\'s one',
};

const Choice = ({ options, value, labels, onPick, busy, name }) => (
  <div className="space-y-2">
    {options.map((option) => {
      const on = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          disabled={busy}
          onClick={() => onPick(option.value)}
          aria-pressed={on}
          aria-label={`${name}: ${labels[option.value] || option.value}`}
          className={`w-full rounded-lg border p-3 text-left transition-colors disabled:opacity-50 ${
            on
              ? 'border-fg-3 '
              : 'border-line hover:bg-elev'
          }`}
        >
          <span className="block text-base font-medium text-fg">
            {labels[option.value] || option.value}
          </span>
          <span className="mt-0.5 block text-sm text-fg-2 leading-relaxed">
            {option.note}
          </span>
        </button>
      );
    })}
  </div>
);

const InsightDials = () => {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/api/advice/prefs')
      .then((res) => setPrefs(res.data))
      .catch(() => setError('Could not load your settings.'));
  }, []);

  const save = async (patch) => {
    setBusy(true);
    setError('');
    try {
      const res = await apiClient.put('/api/advice/prefs', patch);
      setPrefs(res.data);
    } catch (err) {
      setError('Could not save that. Try again?');
    } finally {
      setBusy(false);
    }
  };

  if (!prefs) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-base text-fg-2">
            {error || 'Loading…'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>How much we ask</CardTitle>
          <p className="text-sm text-fg-2 leading-relaxed">
            The daily check-in. Most of what we know about you arrives without
            anyone being asked anything — this is only the part that needs you.
          </p>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <Choice
            name="Daily questions"
            options={prefs.options.thoroughness}
            labels={THOROUGHNESS_LABEL}
            value={prefs.thoroughness}
            busy={busy}
            onPick={(v) => save({ thoroughness: v })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How much we suggest</CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0 space-y-6">
          <div>
            <p className="text-base font-medium text-fg">
              Suggestions from your own data
            </p>
            <p className="mt-1 text-sm text-fg-2 leading-relaxed">
              Small things worth trying, drawn from patterns we've confirmed in
              your record. Off means we keep looking and keep quiet — a record of
              your own body with no opinions attached is a perfectly good way to
              use this.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => save({ advice_enabled: !prefs.advice_enabled })}
              aria-pressed={prefs.advice_enabled}
              className={`mt-3 rounded-lg px-4 py-2 text-base font-medium disabled:opacity-50 ${
                prefs.advice_enabled
                  ? 'bg-cta text-cta-fg'
                  : 'border border-line text-fg'
              }`}
            >
              {prefs.advice_enabled ? 'On' : 'Off'}
            </button>
          </div>

          <div className="pt-6 border-t border-line">
            <p className="text-base font-medium text-fg">
              Experiments
            </p>
            <p className="mt-1 mb-3 text-sm text-fg-2 leading-relaxed">
              Changing one thing for a couple of weeks and measuring it properly.
              One at a time, always — two changes at once can't be told apart
              afterwards.
            </p>
            <Choice
              name="Experiment suggestions"
              options={prefs.options.experiment_appetite}
              labels={APPETITE_LABEL}
              value={prefs.experiment_appetite}
              busy={busy}
              onPick={(v) => save({ experiment_appetite: v })}
            />
          </div>
        </CardContent>
      </Card>

      {error && (
        <p className="text-base text-off-strong dark:text-off">{error}</p>
      )}
    </div>
  );
};

export default InsightDials;
